export interface LibraryRunMedia {
  selfie: string;
  source: string;
  sourcePreview: string;
  output: string;
  outputMp4?: string | null;
}

export interface LibraryRun {
  id: string;
  ownerUserId: string;
  ownerEmail: string;
  state: "QUEUED" | "GENERATING" | "FINALIZING" | "READY" | "FAILED";
  createdAt: string;
  generationSettingsContractVersion: 1;
  generationSettings: import("../../src/generation-settings.ts").GenerationSettings;
  sourceKey: string | null;
  source: string | null;
  sourcePreview: string | null;
  sourceOriginalName: string;
  sourceBytes: number | null;
  outputBytes: number | null;
  failureCode: string | null;
  error: string | null;
  media: LibraryRunMedia | null;
}

export interface LibraryOutput {
  runId: string;
  ownerUserId: string;
  ownerEmail: string;
  createdAt: string;
  generationSettingsContractVersion: 1;
  generationSettings: import("../../src/generation-settings.ts").GenerationSettings;
  output: string;
  outputMp4: string | null;
  outputBytes: number | null;
}

export interface LibraryFailure {
  runId: string;
  ownerUserId: string;
  ownerEmail: string;
  createdAt: string;
  failureCode: string | null;
  error: string | null;
}

export interface LibraryGroup {
  id: string;
  source: string;
  sourcePreview: string;
  sourceOriginalName: string;
  sourceBytes: number | null;
  outputs: LibraryOutput[];
  failures: LibraryFailure[];
}

export function groupLibraryRuns(runs: readonly LibraryRun[]): LibraryGroup[] {
  const groups = new Map<string, LibraryGroup>();
  for (const run of runs) {
    if ((run.state !== "READY" && run.state !== "FAILED") || !run.sourceKey) continue;
    const source = run.source ?? run.media?.source;
    const sourcePreview = run.sourcePreview ?? run.media?.sourcePreview;
    if (!source || !sourcePreview) continue;
    let group = groups.get(run.sourceKey);
    if (!group) {
      group = {
        id: run.sourceKey,
        source,
        sourcePreview,
        sourceOriginalName: run.sourceOriginalName,
        sourceBytes: run.sourceBytes,
        outputs: [],
        failures: [],
      };
      groups.set(run.sourceKey, group);
    }
    if (run.state === "READY" && run.media) {
      group.outputs.push({
        runId: run.id,
        ownerUserId: run.ownerUserId,
        ownerEmail: run.ownerEmail,
        createdAt: run.createdAt,
        generationSettingsContractVersion: run.generationSettingsContractVersion,
        generationSettings: run.generationSettings,
        output: run.media.output,
        outputMp4: run.media.outputMp4 ?? null,
        outputBytes: run.outputBytes,
      });
    } else if (run.state === "FAILED") {
      group.failures.push({
        runId: run.id,
        ownerUserId: run.ownerUserId,
        ownerEmail: run.ownerEmail,
        createdAt: run.createdAt,
        failureCode: run.failureCode,
        error: run.error,
      });
    }
  }
  return [...groups.values()].filter(({ outputs }) => outputs.length > 0);
}

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return "Unavailable";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Number((bytes / 1024).toFixed(1))} KB`;
  return `${Number((bytes / (1024 * 1024)).toFixed(1))} MB`;
}

export function mediaDimensionsLabel(width: number, height: number): string {
  return width > 0 && height > 0 ? `${width} × ${height} px` : "Loading…";
}

export function outputDownloadName(runId: string): string {
  return `gifme-${runId}.gif`;
}

export function preferredOutputDownload(
  runId: string,
  gifPath: string,
  mp4Path: string | null | undefined,
): { path: string; filename: string } {
  const usesMp4 = Boolean(mp4Path);
  return {
    path: usesMp4 ? (mp4Path as string) : gifPath,
    filename: `gifme-${runId}.${usesMp4 ? "mp4" : "gif"}`,
  };
}

export function libraryMediaEvictions<T>(
  cached: readonly T[],
  visible: ReadonlySet<T>,
  targetSize: number,
): T[] {
  const evictions: T[] = [];
  for (const item of cached) {
    if (cached.length - evictions.length <= targetSize) break;
    if (!visible.has(item)) evictions.push(item);
  }
  return evictions;
}
